import React, { useEffect, useState } from 'react';
import { Sidebar } from '../components/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Plus, Edit, Key, Power, PowerOff, Shield, History } from 'lucide-react';

export default function UserManagement() {
  const { getAuthHeaders, user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false);
  const [isAuditOpen, setIsAuditOpen] = useState(false);
  const [auditLogs, setAuditLogs] = useState([]);
  const [editingUser, setEditingUser] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [allowedRoles, setAllowedRoles] = useState([]);
  const [formData, setFormData] = useState({
    username: '',
    name: '',
    role: '',
    email: '',
    mobile_number: '',
    password: ''
  });
  const [editFormData, setEditFormData] = useState({
    name: '',
    email: '',
    mobile_number: '',
    role: ''
  });

  const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

  // Role display names
  const roleDisplayNames = {
    'admin': 'Admin',
    'sub_admin': 'Sub Admin',
    'master_agency': 'Master Agency',
    'agency': 'Agency'
  };

  useEffect(() => {
    fetchUsers();
    fetchAllowedRoles();
  }, []);

  const fetchAllowedRoles = async () => {
    try {
      const response = await axios.get(`${API_URL}/users/allowed-roles`, {
        headers: getAuthHeaders()
      });
      setAllowedRoles(response.data.allowed_roles);
      // Set default role to first allowed role
      if (response.data.allowed_roles.length > 0) {
        setFormData(prev => ({ ...prev, role: response.data.allowed_roles[0] }));
      }
    } catch (error) {
      console.error('Failed to fetch allowed roles');
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API_URL}/users`, {
        headers: getAuthHeaders()
      });
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const fetchAuditLogs = async () => {
    try {
      const response = await axios.get(`${API_URL}/system-audit-logs`, {
        headers: getAuthHeaders()
      });
      setAuditLogs(response.data);
      setIsAuditOpen(true);
    } catch (error) {
      toast.error('Failed to fetch audit logs');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API_URL}/users`, formData, {
        headers: getAuthHeaders()
      });
      toast.success('User created successfully');
      setIsDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setEditFormData({
      name: user.name || '',
      email: user.email || '',
      mobile_number: user.mobile_number || '',
      role: user.role || ''
    });
    setIsEditDialogOpen(true);
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API_URL}/users/${editingUser.id}`, editFormData, {
        headers: getAuthHeaders()
      });
      toast.success('User updated successfully');
      setIsEditDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handlePasswordReset = async () => {
    if (!newPassword || newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }

    try {
      await axios.put(
        `${API_URL}/users/${selectedUser.id}/reset-password`,
        { new_password: newPassword },
        { headers: getAuthHeaders() }
      );
      toast.success('Password reset successfully');
      setIsPasswordDialogOpen(false);
      setNewPassword('');
      setSelectedUser(null);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleToggleStatus = async (userId, currentStatus) => {
    const action = currentStatus ? 'deactivate' : 'activate';
    if (!window.confirm(`Are you sure you want to ${action} this user?`)) return;

    try {
      await axios.put(`${API_URL}/users/${userId}/toggle-status`, null, {
        headers: getAuthHeaders()
      });
      toast.success(`User ${action}d successfully`);
      fetchUsers();
    } catch (error) {
      toast.error(`Failed to ${action} user`);
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      name: '',
      role: allowedRoles.length > 0 ? allowedRoles[0] : '',
      email: '',
      mobile_number: '',
      password: ''
    });
  };

  // Check if current user can manage a specific user
  const canManageUser = (targetUser) => {
    const hierarchy = {
      'admin': ['sub_admin', 'master_agency', 'agency'],
      'sub_admin': ['master_agency', 'agency'],
      'master_agency': ['agency']
    };
    return hierarchy[currentUser?.role]?.includes(targetUser.role) || false;
  };

  const getRoleBadgeColor = (role) => {
    switch(role) {
      case 'admin': return 'bg-purple-100 text-purple-700';
      case 'sub_admin': return 'bg-blue-100 text-blue-700';
      case 'master_agency': return 'bg-amber-100 text-amber-700';
      case 'agency': return 'bg-green-100 text-green-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  return (
    <Sidebar>
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
              <Shield className="w-9 h-9 inline-block mr-2 text-purple-600" />
              User Management
            </h1>
            <p className="text-slate-600 mt-2">
              {currentUser?.role === 'admin' && 'Manage all system users'}
              {currentUser?.role === 'sub_admin' && 'Manage Master Agency and Agency users'}
              {currentUser?.role === 'master_agency' && 'Manage your Agency users'}
            </p>
          </div>
          <div className="flex gap-2">
            {(currentUser?.role === 'admin' || currentUser?.role === 'sub_admin') && (
              <Button variant="outline" onClick={fetchAuditLogs} data-testid="view-audit-logs">
                <History className="w-4 h-4 mr-2" />
                Audit Logs
              </Button>
            )}
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-user-button" className="bg-[#0052CC] hover:bg-[#0043A8] text-white">
                  <Plus className="w-4 h-4 mr-2" />
                  Add User
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Add New User</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="username">Username</Label>
                    <Input
                      id="username"
                      value={formData.username}
                      onChange={(e) => setFormData({...formData, username: e.target.value})}
                      required
                      data-testid="user-username-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="name">Full Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      required
                      data-testid="user-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="role">Role</Label>
                    <Select value={formData.role} onValueChange={(value) => setFormData({...formData, role: value})}>
                      <SelectTrigger data-testid="user-role-select">
                        <SelectValue placeholder="Select Role" />
                      </SelectTrigger>
                      <SelectContent>
                        {allowedRoles.map(role => (
                          <SelectItem key={role} value={role}>{roleDisplayNames[role]}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-slate-500 mt-1">
                      You can create: {allowedRoles.map(r => roleDisplayNames[r]).join(', ')}
                    </p>
                  </div>
                  <div>
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      data-testid="user-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="mobile_number">Mobile Number</Label>
                    <Input
                      id="mobile_number"
                      value={formData.mobile_number}
                      onChange={(e) => setFormData({...formData, mobile_number: e.target.value})}
                      placeholder="+91-9876543210"
                      data-testid="user-mobile-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password">Password</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required
                      data-testid="user-password-input"
                    />
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancel
                    </Button>
                    <Button type="submit" data-testid="user-submit-button" className="bg-[#0052CC] hover:bg-[#0043A8] text-white">
                      Create User
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading users...</div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
            <table className="w-full" data-testid="users-table">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Username</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Name</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Role</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Email</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Mobile</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Status</th>
                  <th className="h-12 px-4 text-left align-middle font-medium text-slate-500 text-xs uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50/50" data-testid={`user-row-${user.id}`}>
                    <td className="p-4 align-middle text-sm text-slate-700 font-medium">{user.username}</td>
                    <td className="p-4 align-middle text-sm text-slate-600">{user.name}</td>
                    <td className="p-4 align-middle text-sm">
                      <span className={`inline-block px-2 py-1 text-xs rounded font-medium ${getRoleBadgeColor(user.role)}`}>
                        {roleDisplayNames[user.role] || user.role.replace('_', ' ').toUpperCase()}
                      </span>
                    </td>
                    <td className="p-4 align-middle text-sm text-slate-600">{user.email || '-'}</td>
                    <td className="p-4 align-middle text-sm text-slate-600">{user.mobile_number || '-'}</td>
                    <td className="p-4 align-middle">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${user.is_active !== false ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {user.is_active !== false ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="p-4 align-middle">
                      {canManageUser(user) ? (
                        <div className="flex items-center gap-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleEdit(user)}
                            data-testid={`edit-user-${user.id}`}
                            title="Edit User"
                          >
                            <Edit className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setSelectedUser(user);
                              setIsPasswordDialogOpen(true);
                            }}
                            data-testid={`reset-password-${user.id}`}
                            title="Reset Password"
                          >
                            <Key className="w-4 h-4 text-amber-600" />
                          </Button>
                          {user.id !== currentUser?.id && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleToggleStatus(user.id, user.is_active !== false)}
                              data-testid={`toggle-status-${user.id}`}
                              title={user.is_active !== false ? 'Deactivate' : 'Activate'}
                            >
                              {user.is_active !== false ? (
                                <PowerOff className="w-4 h-4 text-red-600" />
                              ) : (
                                <Power className="w-4 h-4 text-green-600" />
                              )}
                            </Button>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-slate-400 italic">No access</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {users.length === 0 && (
              <div className="text-center py-12 text-slate-500">
                No users found.
              </div>
            )}
          </div>
        )}

        {/* Edit User Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
          setIsEditDialogOpen(open);
          if (!open) {
            setEditingUser(null);
          }
        }}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Edit User: {editingUser?.username}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleEditSubmit} className="space-y-4">
              <div>
                <Label htmlFor="edit_name">Full Name</Label>
                <Input
                  id="edit_name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({...editFormData, name: e.target.value})}
                  data-testid="edit-user-name-input"
                />
              </div>
              <div>
                <Label htmlFor="edit_email">Email</Label>
                <Input
                  id="edit_email"
                  type="email"
                  value={editFormData.email}
                  onChange={(e) => setEditFormData({...editFormData, email: e.target.value})}
                  data-testid="edit-user-email-input"
                />
              </div>
              <div>
                <Label htmlFor="edit_mobile">Mobile Number</Label>
                <Input
                  id="edit_mobile"
                  value={editFormData.mobile_number}
                  onChange={(e) => setEditFormData({...editFormData, mobile_number: e.target.value})}
                  placeholder="+91-9876543210"
                  data-testid="edit-user-mobile-input"
                />
              </div>
              <div>
                <Label htmlFor="edit_role">Role</Label>
                <Select value={editFormData.role} onValueChange={(value) => setEditFormData({...editFormData, role: value})}>
                  <SelectTrigger data-testid="edit-user-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="admin">Admin</SelectItem>
                    <SelectItem value="sub_admin">Sub Admin</SelectItem>
                    <SelectItem value="master_agency">Master Agency</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" data-testid="edit-user-submit" className="bg-[#0052CC] hover:bg-[#0043A8] text-white">
                  Save Changes
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>

        {/* Password Reset Dialog */}
        <Dialog open={isPasswordDialogOpen} onOpenChange={(open) => {
          setIsPasswordDialogOpen(open);
          if (!open) {
            setNewPassword('');
            setSelectedUser(null);
          }
        }}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Reset Password for {selectedUser?.name}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              <div>
                <Label htmlFor="new_password">New Password</Label>
                <Input
                  id="new_password"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Enter new password (min 6 characters)"
                  data-testid="new-password-input"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsPasswordDialogOpen(false)}>
                  Cancel
                </Button>
                <Button onClick={handlePasswordReset} data-testid="reset-password-submit" className="bg-[#0052CC] hover:bg-[#0043A8] text-white">
                  Reset Password
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Audit Logs Dialog */}
        <Dialog open={isAuditOpen} onOpenChange={setIsAuditOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <History className="w-5 h-5 text-purple-600" />
                User Management Audit Logs
              </DialogTitle>
            </DialogHeader>
            <div className="mt-4">
              {auditLogs.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No audit logs found.</p>
              ) : (
                <div className="space-y-3">
                  {auditLogs.filter(log => 
                    ['user_created', 'user_updated', 'password_reset_by_admin', 'user_status_changed'].includes(log.action_type)
                  ).slice(0, 50).map((log, index) => (
                    <div key={index} className="border border-slate-200 rounded-lg p-3 bg-slate-50">
                      <div className="flex justify-between items-start">
                        <div>
                          <span className={`inline-block px-2 py-0.5 text-xs rounded font-medium ${
                            log.action_type === 'user_created' ? 'bg-green-100 text-green-700' :
                            log.action_type === 'user_status_changed' ? 'bg-amber-100 text-amber-700' :
                            log.action_type === 'password_reset_by_admin' ? 'bg-red-100 text-red-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {log.action_type.replace(/_/g, ' ').toUpperCase()}
                          </span>
                          <p className="text-sm text-slate-700 mt-1">{log.remarks}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-xs text-slate-500">
                            {new Date(log.timestamp).toLocaleString('en-IN')}
                          </p>
                          <p className="text-xs text-slate-400">
                            by {log.changed_by} ({log.user_role})
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Sidebar>
  );
}
